import { localizedStrings } from '@core/strings'
import { Card, CardProps, Chip, Tooltip } from '@common/material'
import React from 'react'
import { Box, Flex, Text } from '@fundamentals'
import { PermitBase } from '@core/entities/permit/PermitBase'
import { PermitStatus } from '@core/entities/permit/PermitBase'
import moment from 'moment'
import EventAvailableOutlined from '@mui/icons-material/EventAvailableOutlined'
import EventBusyOutlined from '@mui/icons-material/EventBusyOutlined'
import LinkIcon from '@mui/icons-material/Link'
import LocationOnOutlined from '@mui/icons-material/LocationOnOutlined'
import PersonOutlined from '@mui/icons-material/PersonOutlined'
import TextSnippetOutlined from '@mui/icons-material/TextSnippetOutlined'
import { User } from '@core/entities/user'
import { UserProfileHoverWrapper } from '@elements/UserProfileHoverWrapper'

type PermitCardProps = Omit<CardProps, 'id'> & {
  templateName: string
  summary: string
  status: PermitStatus
  createdOn?: string
  startsOn?: string
  endsOn?: string
  location?: string
  shortUUID?: string
  uuid?: string
  permitHolder: User
  icon?: React.ReactNode
  isChildPermit?: boolean
}

export const PermitCard: React.FC<PermitCardProps> = ({
  templateName,
  summary,
  status,
  createdOn,
  startsOn,
  endsOn,
  location,
  shortUUID,
  uuid: _omitUuid,
  permitHolder,
  icon,
  isChildPermit,
  sx = {},
  children,
  ...props
}) => {
  return (
    <Card
      sx={{
        pl: 2,
        pr: icon ? 1.5 : 2,
        py: 1.5,
        ...sx,
      }}
      {...props}
    >
      <Flex>
        <Box flex={1}>
          <Flex alignItems='center' gap={0.5}>
            <Text fontSize={13} color='text.secondary'>
              {templateName}
            </Text>
            {isChildPermit && (
              <Tooltip title={localizedStrings.hasLinkedParentPermit}>
                <LinkIcon
                  sx={{ fontSize: 14, color: 'primary.main' }}
                  data-test='child-permit-link-indicator'
                />
              </Tooltip>
            )}
          </Flex>
          <Text fontWeight={500} fontSize='1rem'>
            {summary}
          </Text>
          <Chip
            color={PermitBase.getStatusChipColor(status)}
            label={PermitBase.getStatusString(status)}
            sx={{ mt: 0.5, mb: 1 }}
            size='small'
          />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {!!startsOn && (
              <Flex alignItems='center' sx={{ gap: 3 }}>
                <Flex alignItems='center' sx={{ width: 120 }}>
                  <EventAvailableOutlined
                    sx={{ fontSize: 16, color: 'action.active' }}
                  />
                  <Text color='text.secondary' sx={{ fontSize: 14, ml: 0.5 }}>
                    {localizedStrings.startDate}
                  </Text>
                </Flex>
                <Text sx={{ fontSize: 14, flex: 1 }}>
                  {moment(startsOn).format('DD MMM YYYY [at] HH:mm')}
                </Text>
              </Flex>
            )}
            <Flex alignItems='center' sx={{ gap: 3 }}>
              <Flex alignItems='center' sx={{ width: 120 }}>
                <EventBusyOutlined
                  sx={{ fontSize: 16, color: 'action.active' }}
                />
                <Text color='text.secondary' sx={{ fontSize: 14, ml: 0.5 }}>
                  {localizedStrings.endDate}
                </Text>
              </Flex>
              <Text sx={{ fontSize: 14, flex: 1 }}>
                {endsOn ? moment(endsOn).format('DD MMM YYYY [at] HH:mm') : '-'}
              </Text>
            </Flex>
            {!!location && (
              <Flex alignItems='center' sx={{ gap: 3 }}>
                <Flex alignItems='center' sx={{ width: 120 }}>
                  <LocationOnOutlined
                    sx={{ fontSize: 16, color: 'action.active' }}
                  />
                  <Text color='text.secondary' sx={{ fontSize: 14, ml: 0.5 }}>
                    {localizedStrings.location}
                  </Text>
                </Flex>
                <Text sx={{ fontSize: 14, flex: 1 }}>{location}</Text>
              </Flex>
            )}
            {!!shortUUID && (
              <Flex alignItems='center' sx={{ gap: 3 }}>
                <Flex alignItems='center' sx={{ width: 120 }}>
                  <TextSnippetOutlined
                    sx={{ fontSize: 16, color: 'action.active' }}
                  />
                  <Text color='text.secondary' sx={{ fontSize: 14, ml: 0.5 }}>
                    {localizedStrings.uniqueId}
                  </Text>
                </Flex>
                <Text sx={{ fontSize: 14, flex: 1 }}>{shortUUID}</Text>
              </Flex>
            )}
            <Flex alignItems='center' sx={{ gap: 3 }}>
              <Flex alignItems='center' sx={{ width: 120 }}>
                <PersonOutlined sx={{ fontSize: 16, color: 'action.active' }} />
                <Text color='text.secondary' sx={{ fontSize: 14, ml: 0.5 }}>
                  {localizedStrings.permitHolder}
                </Text>
              </Flex>
              <UserProfileHoverWrapper userId={permitHolder?.id}>
                <Text sx={{ fontSize: 14, flex: 1 }}>
                  {permitHolder.getFullName()}
                </Text>
              </UserProfileHoverWrapper>
            </Flex>
          </Box>
        </Box>
        {icon && <Box>{icon}</Box>}
      </Flex>
      {children}
    </Card>
  )
}
